/**
 * Mortgage calculation utilities
 */

export const calculatePayment = (balance, rate, years, frequency = 'monthly') => {
  try {
    const periods = frequency === 'monthly' ? 12 : frequency === 'biweekly' ? 26 : 52;
    const principal = Number(balance);
    const annualRate = Number(rate);
    const termYears = Number(years);

    if (!principal || !termYears || principal <= 0 || termYears <= 0) {
      return { amount: 0, frequency, monthlyEquivalent: 0 };
    }

    const r = annualRate ? annualRate / 100 / periods : 0;
    const n = termYears * periods;

    if (r === 0) {
      const payment = principal / n;
      return { 
        amount: payment, 
        frequency,
        monthlyEquivalent: payment * periods / 12
      };
    }

    const payment = principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    
    if (!isFinite(payment)) {
      return { amount: 0, frequency, monthlyEquivalent: 0 };
    }
    
    return { 
      amount: payment, 
      frequency,
      monthlyEquivalent: payment * periods / 12
    };
  } catch (error) {
    console.error('Error calculating payment:', error);
    return { amount: 0, frequency: frequency || 'monthly', monthlyEquivalent: 0 };
  }
};

export const calculateDaysToRenewal = (renewalDate) => {
  if (!renewalDate) return 0;
  const renewal = new Date(renewalDate);
  const today = new Date();
  const diffTime = renewal - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

export const calculateHELOC = (homeValue, currentBalance, primaryResidence) => {
  const equity = homeValue - currentBalance;
  const ltv = currentBalance / homeValue;
  const maxLTV = primaryResidence === 'yes' ? 0.80 : 0.65;
  const availableHELOC = Math.max(0, (homeValue * maxLTV) - currentBalance);
  
  return {
    equity,
    ltv: ltv * 100,
    availableHELOC,
    maxLTV: maxLTV * 100
  };
};

export const calculateRecommendationScore = (offer, currentRate, monthlySavings) => {
  let score = 0;
  
  // Rate advantage (40% weight)
  const rateAdvantage = (currentRate - offer.rate) * 10;
  score += rateAdvantage * 0.4;
  
  // Monthly savings (30% weight)
  score += (monthlySavings / 100) * 0.3;
  
  // Term preference (20% weight)
  if (offer.term === 5) score += 5;
  if (offer.term === 3) score += 3;
  
  // Type preference (10% weight)
  if (offer.type === 'fixed') score += 3;
  
  return Math.max(0, score);
};

export const getRenewalRecommendation = (daysToRenewal, currentRate, bestMarketRate, forecast) => {
  let recommendation = 'HOLD';
  let confidence = 'LOW';
  let action = 'Monitor market conditions';
  let risk = 'MEDIUM';
  let reasoning = [];
  
  const rateDifference = currentRate - bestMarketRate;
  
  // Time-based analysis
  if (daysToRenewal <= 30) {
    recommendation = 'RENEW NOW';
    confidence = 'HIGH';
    action = 'Secure renewal immediately';
    risk = 'HIGH';
    reasoning.push('Less than 30 days to renewal - urgent action required');
  } else if (daysToRenewal <= 90) {
    if (rateDifference > 0.5) {
      recommendation = 'RENEW NOW';
      confidence = 'MEDIUM';
      action = 'Lock in better rate now';
      risk = 'MEDIUM';
      reasoning.push('Significant rate improvement available');
    } else {
      recommendation = 'HOLD';
      confidence = 'MEDIUM';
      action = 'Continue monitoring';
      risk = 'LOW';
      reasoning.push('Monitor for better rates in next 30 days');
    }
  } else if (daysToRenewal > 90) {
    if (rateDifference > 1.0) {
      recommendation = 'RENEW NOW';
      confidence = 'HIGH';
      action = 'Lock in significantly better rate';
      risk = 'LOW';
      reasoning.push('Exceptional rate opportunity');
    } else {
      recommendation = 'HOLD';
      confidence = 'MEDIUM';
      action = 'Wait for market movements';
      risk = 'LOW';
      reasoning.push('Time available to find better rates');
    }
  }
  
  return {
    recommendation,
    confidence,
    action,
    risk,
    reasoning,
    potentialSavings: rateDifference > 0 ? rateDifference : 0
  };
};